import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';
import type { ClassReportData } from '../../components/timetable/ClassReport';
import { adminBase } from './adminBase';

export interface MonitoringSlot {
	id: string;
	class: any;
	periodTemplate: any;
	trainers: any[];
	dayBucket: string;
	startsAt: string;
	endsAt: string;
	status: string;
	derivedStatus:
		| 'completed'
		| 'in_progress'
		| 'warning'
		| 'missed_red'
		| 'missed_pending_ack'
		| 'missed_resolved'
		| 'cancelled'
		| 'upcoming';
	sessionLog: any | null;
	review: any | null;
}

export interface SlotMessage {
	id: string;
	slot: string;
	fromUser: string;
	fromRole: string;
	fromName: string;
	body: string;
	createdAt: string;
}

export const masterAdminMonitoringApi = createApi({
	reducerPath: 'masterAdminMonitoringApi',
	baseQuery: axiosBaseQuery,
	tagTypes: ['MonToday', 'MonWeek', 'MonAction', 'SlotMessages', 'SlotReview', 'MonOverview'],
	endpoints: (builder) => ({
		getMonOverview: builder.query<
			{
				generatedAt: string;
				totals: Record<string, number>;
				schools: Array<{
					id: string;
					fullName: string;
					timezone: string;
					status: string;
					dayBucket: string;
					counts: Record<string, number>;
					completionPct: number;
					needsAttention: number;
				}>;
			},
			void
		>({
			query: () => ({ url: `${adminBase()}/monitoring/overview` }),
			providesTags: ['MonOverview'],
		}),
		getMonToday: builder.query<
			{ dayBucket: string; timezone: string; slots: MonitoringSlot[]; counts: Record<string, number> },
			{ schoolId: string }
		>({
			query: ({ schoolId }) => ({
				url: `${adminBase()}/monitoring/today`,
				params: { schoolId },
			}),
			providesTags: ['MonToday'],
		}),
		getMonWeek: builder.query<
			{
				weekStartDate?: string;
				startDate?: string;
				endDate?: string;
				timezone: string;
				slots: MonitoringSlot[];
				perTrainer: any[];
				perClass: any[];
			},
			{
				schoolId: string;
				weekStartDate?: string;
				startDate?: string;
				endDate?: string;
			}
		>({
			query: ({ schoolId, weekStartDate, startDate, endDate }) => ({
				url: `${adminBase()}/monitoring/week`,
				params: { schoolId, weekStartDate, startDate, endDate },
			}),
			providesTags: ['MonWeek'],
		}),
		getMonActionRequired: builder.query<
			{ items: MonitoringSlot[] },
			{ schoolId: string }
		>({
			query: ({ schoolId }) => ({
				url: `${adminBase()}/monitoring/action-required`,
				params: { schoolId },
			}),
			providesTags: ['MonAction'],
		}),
		reviewMissed: builder.mutation<
			any,
			{ slotId: string; decision: 'accepted' | 'rejected'; adminNote?: string }
		>({
			query: ({ slotId, ...body }) => ({
				url: `${adminBase()}/monitoring/slots/${slotId}/review`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['MonAction', 'MonToday', 'MonWeek', 'SlotReview'],
		}),
		getSlotReport: builder.query<ClassReportData, string>({
			query: (slotId) => ({
				url: `${adminBase()}/monitoring/slots/${slotId}/report`,
			}),
		}),
		listSlotMessages: builder.query<{ messages: SlotMessage[] }, string>({
			query: (slotId) => ({
				url: `${adminBase()}/monitoring/slots/${slotId}/messages`,
			}),
			providesTags: (_r, _e, slotId) => [{ type: 'SlotMessages', id: slotId }],
		}),
		postSlotMessage: builder.mutation<any, { slotId: string; body: string }>({
			query: ({ slotId, body }) => ({
				url: `${adminBase()}/monitoring/slots/${slotId}/messages`,
				method: 'POST',
				data: { body },
			}),
			invalidatesTags: (_r, _e, { slotId }) => [{ type: 'SlotMessages', id: slotId }],
		}),
	}),
});

export const {
	useGetMonOverviewQuery,
	useGetMonTodayQuery,
	useGetMonWeekQuery,
	useGetMonActionRequiredQuery,
	useReviewMissedMutation,
	useGetSlotReportQuery,
	useListSlotMessagesQuery,
	usePostSlotMessageMutation,
} = masterAdminMonitoringApi;
